import { Assignment } from '@peekapak/types/LessonPlanTypes';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import {
  ReportStudent,
  TeacherClassroomMap,
  TeacherClassroomMaps,
  TeacherStudentMap,
} from '@peekapak/types/ReportDataExchangeTypes';
import { getLocalForage, setLocalForage } from '../../aaa';
import { getClassrooms, listStudentAssignments } from '../../BackendInterface';
import { filterStudentObjectByDate, getTimestampFromDate } from '../reportUtilities';

//////////////////////////////////////////////////////////////////
//
// A school's rosters and assignments, loaded exactly as the school report
// loads them. The district report's PDF calls the same functions for each
// school on the district's list, so its Assignments number is the same
// count the school reports produce - and both share these caches, so a
// school loaded by either report is already warm for the other.
//
// windowStart/windowEnd are the report's fetch window
// (getSchoolYearDateBounds + getReportFetchWindow).
//
//////////////////////////////////////////////////////////////////

const tableOverrideSuffix = '';

// Classrooms are fetched in parallel batches rather than one teacher at a time - one at a time scales
// linearly with roster size, which for a school with hundreds of teachers could take minutes - while
// still capping how many requests hit the backend at once.
const CLASSROOM_FETCH_BATCH_SIZE = 10;

export async function loadTeacherMaps(
  schoolId: string,
  users: UserProfileType[],
  windowStart: string,
  windowEnd: string,
  onBatch?: (done: number, total: number) => void,
): Promise<{ teacherStudentMap: TeacherStudentMap; teacherClassroomMaps: TeacherClassroomMaps }> {
  const teacherStudentMapStorageKey = `${schoolId}-teacherStudentMap-from-${windowStart}-to-${windowEnd}`;
  const teacherClassroomMapsStorageKey = `${schoolId}-teacherClassroomMaps-from-${windowStart}-to-${windowEnd}`;
  const cachedTeacherStudentMap = JSON.parse((await getLocalForage(teacherStudentMapStorageKey)) as string);
  const cachedTeacherClassroomMaps = JSON.parse((await getLocalForage(teacherClassroomMapsStorageKey)) as string);

  if (cachedTeacherStudentMap && cachedTeacherClassroomMaps) {
    return { teacherStudentMap: cachedTeacherStudentMap, teacherClassroomMaps: cachedTeacherClassroomMaps };
  }

  const teachers = users.filter((e) => e.type === 'Teacher' || e.type === 'SchoolAdmin' || e.type === 'DistrictAdmin');
  const teacherToStudentsMap: TeacherStudentMap = {};
  const teacherToClassroomMapList: TeacherClassroomMap[] = [];
  const startingTimestamp = getTimestampFromDate(windowStart);
  const endingTimestamp = getTimestampFromDate(windowEnd);

  let i = 0;
  while (i < teachers.length) {
    const batch = teachers.slice(i, i + CLASSROOM_FETCH_BATCH_SIZE);
    onBatch?.(Math.min(i + CLASSROOM_FETCH_BATCH_SIZE, teachers.length), teachers.length);

    const batchClassrooms = await Promise.all(
      batch.map((teacher) => getClassrooms(teacher.email, tableOverrideSuffix)),
    );

    batch.forEach((teacher, batchIndex) => {
      const classrooms = batchClassrooms[batchIndex];
      const list: ReportStudent[] = [];

      classrooms.forEach((c) => {
        if (c.createdAt >= startingTimestamp && c.createdAt <= endingTimestamp) {
          if (c.listOfStudentProxies) {
            c.listOfStudentProxies.forEach((p) => {
              const { firstName, middleName, lastName, userId, username } = p;
              const studentToStore = {
                firstName,
                middleName: middleName || '',
                lastName: lastName || '',
                userId,
                username,
                grade: parseInt(String(c.grade)),
                language: c.language,
              };

              list.push(studentToStore);
            });
          }
        }
      });

      teacherToStudentsMap[teacher.email] = list;

      teacherToClassroomMapList.push({
        name: `${teacher.firstName} ${teacher.lastName}`,
        email: teacher.email,
        userId: teacher.userId,
        classrooms,
      });
    });

    i += CLASSROOM_FETCH_BATCH_SIZE;
  }

  await setLocalForage(teacherStudentMapStorageKey, JSON.stringify(teacherToStudentsMap), 1440);

  teacherToClassroomMapList.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

  const newTeacherClassroomMaps = {
    school: schoolId,
    maps: teacherToClassroomMapList,
  };

  await setLocalForage(teacherClassroomMapsStorageKey, JSON.stringify(newTeacherClassroomMaps), 60);

  return { teacherStudentMap: teacherToStudentsMap, teacherClassroomMaps: newTeacherClassroomMaps };
}

// Every secondary (grade 6+) student on the school's rosters -> their assignments. A student whose
// lookup fails is left out, rather than failing the whole map.
export async function loadAssignmentsMap(
  schoolId: string,
  teacherStudentMap: TeacherStudentMap,
  windowStart: string,
  windowEnd: string,
  options: {
    // How many students are looked up at once. The school report goes one at a time; the district
    // report, which covers many schools, runs a few side by side. The resulting map is the same.
    concurrency?: number;
    onProgress?: (done: number, total: number) => void;
    isCancelled?: () => boolean;
  } = {},
): Promise<Record<string, Assignment[]> | null> {
  const storageKey = `${schoolId}-assignments-from-${windowStart}-to-${windowEnd}`;
  const map = await getLocalForage(storageKey);
  if (map) return map as Record<string, Assignment[]>;

  const students = Object.values(teacherStudentMap).reduce<ReportStudent[]>((acc, curr) => {
    const secondaryOnly = curr.filter((item) => parseInt(String(item.grade)) > 5);
    return [...acc, ...secondaryOnly];
  }, []);

  const newMap: Record<string, Assignment[]> = {};
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < students.length) {
      if (options.isCancelled?.()) return;
      const student = students[next++];
      try {
        newMap[student.userId] = await listStudentAssignments(
          student.userId,
          student.language,
          student.grade,
          tableOverrideSuffix,
        );
      } catch (e) {
        console.error(e);
      }
      done += 1;
      options.onProgress?.(done, students.length);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, options.concurrency ?? 1) }, worker));

  // A cancelled load is incomplete - don't cache it, so the next load starts over.
  if (options.isCancelled?.()) return null;

  await setLocalForage(storageKey, newMap, 1440);
  return newMap;
}

// The school PDF's "Assignments" number: every assignment record in the map that falls in the picked
// range (computeSchoolDeckMetrics counts Object.values(dateFilteredAssignments).flat()).
export const countAssignmentsInRange = (assignmentsMap: Record<string, Assignment[]>, from: Date, to: Date) =>
  Object.values(filterStudentObjectByDate(from, to, assignmentsMap)).flat().length;
